import React from 'react';
import Link from 'next/link';
import omit from 'lodash/omit';
import Button from '@mui/material/Button';
import GeneralMenu from '@/components/navigation/GeneralMenu';
import GeneralChart from '@/components/seo/GeneralChart';
import SvgElectric from '../SvgElectric';
import SvgCulture from './SvgCulture';
import { AuthContext, JobTitleContext } from '@/context/create-context';
import {
  CULTURAL_ROUTER_PATH,
  CULTURAL_QUIZ_ROUTER_PATH,
  RESUME_EVALUATION_ROUTER_PATH,
} from '@/constants/router-path';
import { POSITION_OVERVIEW_ITEMS } from '@/constants/job-title';

const ENTRY_STAGES = ['必备技能', '应届毕业生', '技术转行者', '其他转行者'];

const EntrySwitcher = ({
  data,
}: {
  data: Record<string, Record<string, any>>;
}) => {
  const [activeStage, setActiveStage] = React.useState(ENTRY_STAGES[0]);

  return (
    <>
      <div className="flex gap-10 my-4">
        {ENTRY_STAGES.map((stage, index) => (
          <span
            key={index}
            onClick={() => setActiveStage(stage)}
            className={`transition-colors cursor-pointer font-semibold ${
              activeStage === stage
                ? 'border-b border-primary border-solid'
                : 'text-web-grey-600 hover:text-primary'
            }`}
          >
            {stage}
          </span>
        ))}
      </div>
      {Object.entries(data).map(([key, val], index) => (
        <div
          key={key + index}
          className={activeStage === key ? 'block' : 'max-h-0 overflow-hidden'}
        >
          <p className="mb-2">{val['说明'] || val['总体说明'] || ''}</p>
          <ul className={`list-inside !ml-2`}>
            {key === ENTRY_STAGES[0]
              ? (val['技能清单'] || []).map(
                  (item: Record<string, any>, index: number) => (
                    <li
                      key={index}
                    >{`${item['类别'] || ''}：${(item['条目'] || []).join('、')}`}</li>
                  ),
                )
              : (val['要点清单'] || []).map((item: string, index: number) => (
                  <li key={index}>{item || ''}</li>
                ))}
          </ul>
        </div>
      ))}
    </>
  );
};

const PositionOverview = (props: {
  show: boolean;
  jobData: Record<string, any>;
  aiReplacementData: Record<string, any>;
  marketData: Record<string, any>;
  fitData: Record<string, any>;
  entryData: Record<string, any>;
}) => {
  const { show, jobData, aiReplacementData, marketData, fitData, entryData } =
    props;

  const { displayName } = React.useContext(JobTitleContext);
  const { isLogin } = React.useContext(AuthContext);

  return (
    <div
      className={`relative ${show ? 'max-h-full' : 'max-h-0 overflow-hidden'}`}
    >
      <GeneralMenu menuList={POSITION_OVERVIEW_ITEMS} />
      <h2 className="text-xl font-semibold">作为求职者，应如何看待这个职位</h2>
      <GeneralChart
        id={POSITION_OVERVIEW_ITEMS[0].id}
        title="这个职位是做什么的？"
        tip={jobData['tip']}
      >
        <h4 className="font-semibold mb-2">职业角色</h4>
        <p>{jobData['职业角色'] || ''}</p>
        <h4 className="font-semibold mt-4 mb-2">主要职责</h4>
        <ul className="list-inside !ml-2">
          {(jobData['主要职责'] || []).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <h4 className="font-semibold mt-4 mb-2">行业覆盖</h4>
        <p>{jobData['行业适用性'] || ''}</p>
      </GeneralChart>
      <GeneralChart
        id={POSITION_OVERVIEW_ITEMS[1].id}
        title={`AI时代，${displayName}会被取代吗？`}
        tip={aiReplacementData['tip']}
      >
        <h4 className="font-semibold mb-2">哪些工作正在被AI改变</h4>
        <p className="mb-2">
          {aiReplacementData['哪些工作正在被AI改变']?.['总体说明'] || ''}
        </p>
        <ul className="list-inside !ml-2">
          {(aiReplacementData['哪些工作正在被AI改变']?.['示例条目'] || []).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            ),
          )}
        </ul>
        <h4 className="font-semibold mt-4 mb-2">哪些工作是新的机遇</h4>
        <p className="mb-2">
          {aiReplacementData['哪些工作是新的机遇']?.['总体说明'] || ''}
        </p>
        <ul className="list-inside !ml-2">
          {(aiReplacementData['哪些工作是新的机遇']?.['示例条目'] || []).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            ),
          )}
        </ul>
        <h4 className="font-semibold mt-4 mb-2">必须掌握提升的新技能</h4>
        <p className="mb-2">
          {aiReplacementData['必须掌握的新技能']?.['总体说明'] || ''}
        </p>
        <ul className="list-inside !ml-2">
          {(aiReplacementData['必须掌握的新技能']?.['技能建议'] || []).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            ),
          )}
        </ul>
      </GeneralChart>
      <GeneralChart
        id={POSITION_OVERVIEW_ITEMS[2].id}
        title="如何解读行业前景与市场需求？"
        tip={marketData['tip']}
      >
        <h4 className="font-semibold mb-2">市场需求总体态势</h4>
        <p className="mb-2">
          {marketData['市场需求总体态势']?.['总体说明'] || ''}
        </p>
        <ul className="list-inside !ml-2">
          {Object.entries(marketData['市场需求总体态势'] || {}).map(
            ([key, value], index) => (
              <li
                key={key + index}
              >{`${key.replace('需求广泛性', '需求覆盖哪些行业').replace('增长驱动因素', '机会集中在哪些行业').replace('岗位定位与稳定性', '岗位稳定性分析')}： ${value}`}</li>
            ),
          )}
        </ul>
        <h4 className="font-semibold mt-4 mb-2">热门行业发展</h4>
        <table className="w-full table-fixed text-sm text-left">
          <thead className="bg-web-grey-300 border-b font-semibold border-web-tertiary">
            <tr>
              <th className="py-2 pl-3">
                {`热门 Top${(marketData['热门行业发展']?.['行业对比'] || []).length}`}
              </th>
              <th className="py-2 px-3">核心业务场景</th>
              <th className="py-2 px-3 w-52">技术侧重要求</th>
              <th className="py-2 px-3 w-48">发展特点</th>
            </tr>
          </thead>
          <tbody>
            {(marketData['热门行业发展']?.['行业对比'] || []).map(
              (item: Record<string, string>, index: number) => (
                <tr key={index} className="border-b border-web-tertiary">
                  <td className="py-2 pl-3">{item['行业名称'] || ''}</td>
                  <td className="py-2 px-3">{item['核心业务场景'] || ''}</td>
                  <td className="py-2 px-3 w-48">
                    {item['技术侧重点要求'] || ''}
                  </td>
                  <td className="py-2 px-3 w-52">{item['发展特点'] || ''}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
        {/*<h4 className="font-semibold mt-4 mb-2">如何选择合适的行业</h4>*/}
        {/*<p className="">{marketData['如何选择合适的行业'] || ''}</p>*/}
      </GeneralChart>
      <GeneralChart
        id={POSITION_OVERVIEW_ITEMS[3].id}
        title={`我适合做${displayName}吗？`}
        tip={fitData['tip']}
      >
        <h4 className="font-semibold mb-2">什么样的人更适合这个岗位</h4>
        <p className="mb-2">
          {fitData['什么样的人更适合这个岗位']?.['总体说明'] || ''}
        </p>
        <ul className="list-inside !ml-2">
          {(fitData['什么样的人更适合这个岗位']?.['特征描述'] || []).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            ),
          )}
        </ul>
        <Link
          className="block rounded-lg my-4 p-px text-sm [background:linear-gradient(180deg,#3F73D3_0%,#237EA3_95.62%)] group"
          href={RESUME_EVALUATION_ROUTER_PATH}
          target="_blank"
        >
          <div className="bg-white rounded-lg px-4 py-2 flex items-center">
            <SvgElectric className="shrink-0 mr-2.5" />
            <span className="group-hover:underline">
              立即上传简历查看我的描述匹配岗位“软要求”吗？
            </span>
          </div>
        </Link>
        <h4 className="font-semibold mt-4 mb-2">哪些人可能不太适合</h4>
        <p className="mb-2">
          {fitData['哪些人可能不适合']?.['总体说明'] || ''}
        </p>
        <ul className="list-inside !ml-2">
          {(fitData['哪些人可能不适合']?.['特征描述'] || []).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            ),
          )}
        </ul>
      </GeneralChart>
      <div className="my-8 px-12 py-4 rounded-lg flex justify-between bg-[linear-gradient(94.46deg,rgba(231,28,136,0.04)_4.39%,rgba(141,19,211,0.04)_94.78%)]">
        <div>
          <p className="flex items-center font-semibold">
            <SvgCulture className="shrink-0 mr-3" />
            <span>企业文化匹配测试</span>
          </p>
          <p className="text-xs mt-2 text-web-grey-600">
            帮你找到最适合的企业类型和目标公司
          </p>
        </div>
        <Button
          variant="contained"
          className="!w-40 !h-[42px] !bg-white !text-primary hover:!bg-web-neutral-300"
          LinkComponent={Link}
          href={isLogin ? CULTURAL_QUIZ_ROUTER_PATH : CULTURAL_ROUTER_PATH}
        >
          立即测试
        </Button>
      </div>
      <GeneralChart
        id={POSITION_OVERVIEW_ITEMS[4].id}
        title="如何入行"
        tip={entryData['tip']}
      >
        <EntrySwitcher data={omit(entryData, ['tip'])} />
      </GeneralChart>
    </div>
  );
};
export default PositionOverview;
